import type { LucideIcon } from 'lucide-react';

import { isParent } from '@repo/lib/user-lvl/isParent';
import { NotificationType } from '@repo/types/enums/notificationType';
import { UserLvl } from '@repo/types/enums/userLvl';
import { BarChartBig, Bell, CalendarDays, CalendarRange, ClipboardCheckIcon, ClipboardListIcon, Folder, Home, LayoutListIcon, MessageCircle, Settings2, Users } from 'lucide-react';
import { ComponentProps, useMemo } from 'react';

import { NavHeader } from '@/components/features/app-sidebar/NavHeader';
import { NavMain } from '@/components/features/app-sidebar/NavMain';
import { NavUser } from '@/components/features/app-sidebar/NavUser';
import { useNotificationsSheet } from '@/components/features/notifications/NotificationSheetProvider';
import { Sidebar, SidebarContent, SidebarFooter } from '@/components/ui/sidebar/Sidebar';
import { useCredentials } from '@/store/auth-store';

export interface NavMainItem {
  title: string;
  url?: string;
  notificationType?: NotificationType;
  icon?: LucideIcon;
  isActive?: boolean;
  onClick?: () => void;
  items?: {
    title: string;
    url?: string;
    onClick?: () => void;
  }[];
}

export function AppSidebar(appSidebarProps: ComponentProps<typeof Sidebar>) {
  const { lvl, accountType } = useCredentials();
  const { onOpenChange } = useNotificationsSheet();

  const data = useMemo(() => {
    // Parents carry UserLvl.STUDENT, so this has to come before any lvl
    // branching or they would pick up the student navigation.
    if (isParent(accountType)) {
      const parentNavMain: NavMainItem[] = [
        {
          title: 'Strona Główna',
          url: '/parent',
          icon: Home,
        },
        {
          title: 'Powiadomienia',
          notificationType: NotificationType.All,
          onClick: () => onOpenChange(true),
          icon: Bell,
        },
        {
          title: 'Chat',
          notificationType: NotificationType.Chat,
          url: '/chat',
          icon: MessageCircle,
        },
        {
          title: 'Kalendarz',
          url: '/parent/calendar',
          icon: CalendarDays,
        },
        {
          title: 'Plan zajęć',
          url: '/parent/schedule',
          icon: CalendarRange,
        },
        {
          title: 'Oceny',
          notificationType: NotificationType.Grade,
          url: '/parent/grades',
          icon: BarChartBig,
        },
        {
          title: 'Quizy',
          url: '/parent/quizzes',
          icon: LayoutListIcon,
        },
        {
          title: 'Zadania',
          url: '/parent/assignments',
          icon: ClipboardListIcon,
        },
        {
          title: 'Frekwencja',
          url: '/parent/attendance',
          icon: ClipboardCheckIcon,
        },
        {
          title: 'Grupy',
          url: '/parent/groups',
          icon: Users,
        },
        {
          title: 'Nauczyciele',
          url: '/parent/teachers',
          icon: Users,
        },
      ];

      return { navMain: parentNavMain };
    }

    const navMain: NavMainItem[] = [
      {
        title: 'Strona Główna',
        url: '/',
        icon: Home,
        notificationType: NotificationType.Post,
      },
      {
        title: 'Powiadomienia',
        notificationType: NotificationType.All,
        onClick: () => onOpenChange(true),
        icon: Bell,
      },
      {
        title: 'Chat',
        notificationType: NotificationType.Chat,
        url: '/chat',
        icon: MessageCircle,
      },
      {
        title: 'Kalendarz',
        notificationType: NotificationType.Calendar,
        url: '/calendar',
        icon: CalendarDays,
      },
      {
        title: 'Grupy',
        notificationType: NotificationType.Group,
        url: '/groups',
        icon: Users,
      },
      ...(lvl === UserLvl.STUDENT
        ? [
            {
              title: 'Oceny',
              notificationType: NotificationType.Grade,
              url: '/grades',
              icon: BarChartBig,
            },
          ]
        : []),
      {
        title: 'Quizy',
        url: '/quizzes',
        icon: LayoutListIcon,
      },
      {
        title: 'Zadania',
        url: '/assignments',
        icon: ClipboardListIcon,
      },
      {
        title: 'Pliki',
        url: '/fs',
        icon: Folder,
        notificationType: NotificationType.Fs,
      },
    ];

    if (lvl >= 1) {
      navMain.splice(4, 0, {
        title: 'Plan zajęć',
        url: '/schedule',
        icon: CalendarRange,
      });

      let manageItems: NavMainItem[] = [
        { title: 'Użytkownicy', url: '/manage/users' },
        { title: 'Grupy', url: '/manage/groups' },
        { title: 'Posty', url: '/manage/posts' },
        { title: 'Quizy', url: '/manage/quizzes' },
        { title: 'Zadania', url: '/manage/assignments' },
        { title: 'Rodzice', url: '/manage/parents' },
      ];

      if (lvl >= 2) {
        manageItems = [...manageItems, { title: 'Lokalizacje', url: '/manage/locations' }, { title: 'Dni wolne', url: '/manage/holidays' }];
      }

      navMain.push({
        title: 'Zarządzanie',
        url: '/manage',
        icon: Settings2,
        items: manageItems,
      });
    }

    return { navMain };
  }, [lvl, accountType, onOpenChange]);

  return (
    <>
      <Sidebar collapsible={'icon'} variant={'floating'} {...appSidebarProps}>
        <NavHeader />
        <SidebarContent>
          <NavMain items={data.navMain} />
        </SidebarContent>
        <SidebarFooter className={'border-border-primary border-t p-0'}>
          <NavUser />
        </SidebarFooter>
      </Sidebar>
    </>
  );
}
